/**
 * Clerk Authentication Provider
 * 
 * Wraps the application with Clerk authentication context.
 * Handles sign-in, sign-out, and session management.
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { 
  ClerkProvider as ClerkProviderBase, 
  SignedIn as ClerkSignedIn, 
  SignedOut as ClerkSignedOut, 
  RedirectToSignIn as ClerkRedirectToSignIn,
  SignIn as ClerkSignIn,
  SignUp as ClerkSignUp,
  UserButton as ClerkUserButton,
  useUser, 
  useAuth 
} from '@clerk/clerk-react';
import { config, isClerkConfigured } from '../config';
import { Button } from '@/components/ui/button';
import { KeyRound, RefreshCw } from 'lucide-react';

// Get Clerk config from runtime config
const clerkPubKey = config.clerkPublishableKey;
const clerkTokenTemplate = config.clerkTokenTemplate;

if (!isClerkConfigured()) {
  if (import.meta.env.DEV) {
    console.warn('Missing CLERK_PUBLISHABLE_KEY - auth will be disabled');
  }
}

interface ClerkProviderProps {
  children: React.ReactNode;
}

/**
 * Main Clerk provider wrapper
 * Falls back to rendering children directly if Clerk is not configured (development mode)
 */
export const ClerkProvider: React.FC<ClerkProviderProps> = ({ children }) => {
  // If no Clerk key, render without auth (development mode)
  if (!isClerkConfigured()) {
    return <>{children}</>;
  }

  return (
    <ClerkSessionErrorBoundary>
      <ClerkProviderBase publishableKey={clerkPubKey}>
        {children}
      </ClerkProviderBase>
    </ClerkSessionErrorBoundary>
  );
};

/**
 * Error boundary that catches Clerk session/JWKS errors and recovers
 * by clearing stale session data and prompting re-authentication.
 */
class ClerkSessionErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null; componentStack: string | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Clerk session error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    this.setState({ componentStack: errorInfo.componentStack ?? null });
  }

  handleRecovery = () => {
    // Clear all Clerk-related session data
    try {
      // Clear cookies for the current domain
      document.cookie.split(';').forEach((c) => {
        const name = c.trim().split('=')[0];
        if (name && (name.startsWith('__clerk') || name.startsWith('__session') || name.startsWith('__client'))) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
      // Clear Clerk entries from localStorage and sessionStorage
      for (const store of [localStorage, sessionStorage]) {
        const keysToRemove: string[] = [];
        for (let i = 0; i < store.length; i++) {
          const key = store.key(i);
          if (key && (key.startsWith('clerk') || key.startsWith('__clerk'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => store.removeItem(k));
      }
    } catch {
      // Storage access may fail in some contexts
    }
    // Reload to get a clean Clerk initialization
    window.location.href = '/sign-in';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isJwksError = this.state.error?.message?.includes('JWKS')
        || this.state.error?.message?.includes('Key not found');

      // This boundary wraps ClerkProviderBase, which renders the entire app
      // as its children -- so as a React error boundary, it catches ANY
      // uncaught render exception anywhere in the app, not just genuine
      // Clerk/session problems. A live crash (a canvas gradient color-parse
      // error in an unrelated dashboard chart) got caught here and was
      // shown to the user as "An authentication error occurred," with the
      // only recovery action being a forced Clerk sign-out + sign-in
      // redirect -- actively wrong and confusing for a bug that had nothing
      // to do with the user's session. Only the specific JWKS/session
      // signature below is treated as an auth problem; everything else is
      // an ordinary unexpected error, with "Reload page" (not a forced
      // sign-out) as the primary recovery action.
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
          <div className="max-w-md space-y-4">
            <div className="text-4xl"><KeyRound size={36} className="mx-auto text-muted-foreground" /></div>
            <h2 className="text-xl font-semibold text-foreground">
              {isJwksError ? 'Session Expired' : 'Something Went Wrong'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isJwksError
                ? 'Your session could not be verified. This usually happens when signing keys are rotated. Please sign in again.'
                : `An unexpected error occurred: ${this.state.error?.message ?? 'Unknown error'}`}
            </p>
            {!isJwksError && this.state.componentStack && (
              <details className="text-left">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Component trace</summary>
                <pre className="mt-1 max-h-40 overflow-auto rounded border border-border bg-muted p-2 text-[10px] leading-relaxed text-muted-foreground whitespace-pre-wrap">
                  {this.state.componentStack.trim()}
                </pre>
              </details>
            )}
            {isJwksError ? (
              <Button onClick={this.handleRecovery} size="lg" className="gap-2">
                Sign In Again →
              </Button>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Button onClick={this.handleReload} size="lg" className="gap-2">
                  <RefreshCw size={16} /> Reload Page
                </Button>
                <button
                  onClick={this.handleRecovery}
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                >
                  Still stuck? Sign in again instead
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Protected route wrapper - redirects to sign-in if not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, fallback }) => {
  // If Clerk is not configured, allow access (development mode)
  if (!isClerkConfigured()) {
    return <>{children}</>;
  }

  return (
    <>
      <SignedIn>
        {children}
      </SignedIn>
      <SignedOut>
        {fallback || <RedirectToSignIn />}
      </SignedOut>
    </>
  );
};

/**
 * Hook to get current user info with tenant data
 */
export interface CurrentUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  imageUrl: string;
  tenantId: string | null;
  role: string;
  isLoaded: boolean;
  isSignedIn: boolean;
}

export const useCurrentUser = (): CurrentUser => {
  // If Clerk not configured, return mock user for development
  // IMPORTANT: Check this BEFORE calling useUser() since Clerk hooks
  // require ClerkProvider context
  if (!isClerkConfigured()) {
    return {
      id: 'dev-user-001',
      email: 'dev@localhost',
      firstName: 'Developer',
      lastName: 'User',
      fullName: 'Developer User',
      imageUrl: '',
      tenantId: '00000000-0000-0000-0000-000000000001', // Default dev tenant
      role: 'owner',
      isLoaded: true,
      isSignedIn: true,
    };
  }

  // Only call Clerk hooks when Clerk is configured
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { user, isLoaded, isSignedIn } = useUser();

  if (!isLoaded || !isSignedIn || !user) {
    return {
      id: '',
      email: '',
      firstName: null,
      lastName: null,
      fullName: null,
      imageUrl: '',
      tenantId: null,
      role: 'viewer',
      isLoaded,
      isSignedIn: isSignedIn ?? false,
    };
  }

  // Extract tenant_id and role from Clerk public metadata
  // These are set via Clerk dashboard or webhook when user signs up
  const publicMetadata = user.publicMetadata as {
    tenant_id?: string;
    role?: string;
  } | undefined;

  return {
    id: user.id,
    email: user.primaryEmailAddress?.emailAddress ?? '',
    firstName: user.firstName,
    lastName: user.lastName,
    fullName: user.fullName,
    imageUrl: user.imageUrl,
    tenantId: publicMetadata?.tenant_id ?? null,
    role: publicMetadata?.role ?? 'viewer',
    isLoaded: true,
    isSignedIn: true,
  };
};

/**
 * Hook to get auth token for API calls
 */
export const useAuthToken = () => {
  // If Clerk not configured, return mock token for development
  // IMPORTANT: Check this BEFORE calling useAuth() since Clerk hooks
  // require ClerkProvider context
  if (!isClerkConfigured()) {
    // Return stable references for dev mode
    return {
      getToken: devModeGetToken,
      isLoaded: true,
      isSignedIn: true,
    };
  }

  // Only call Clerk hooks when Clerk is configured
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { getToken, isLoaded, isSignedIn } = useAuth();

  // Wrap getToken to handle errors, but use useCallback to stabilize reference
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const stableGetToken = React.useCallback(async () => {
    try {
      if (clerkTokenTemplate) {
        return await getToken({ template: clerkTokenTemplate });
      }
      return await getToken();
    } catch (error) {
      // Don't log — rethrow silently or use error boundary
      throw error;
    }
  }, [getToken]);

  // Use useMemo to return a stable object reference
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return React.useMemo(() => ({
    getToken: stableGetToken,
    isLoaded,
    isSignedIn,
  }), [stableGetToken, isLoaded, isSignedIn]);
};

// Stable reference for dev mode token getter (defined outside component)
const devModeGetToken = async () => 'dev-token';

/**
 * Thrown by `getAuthTokenOrThrow` when the user is expected to be signed in
 * (Clerk's own `isSignedIn` says so) but no token could be obtained after
 * retrying. Callers should catch this SPECIFICALLY and surface `.message`
 * directly to the user instead of sending a request with no Authorization
 * header -- an unauthenticated request to an endpoint that requires auth
 * just produces the backend's generic, unhelpful "Authentication failed"
 * (see jwt_auth.rs's JwtError::to_response), with no indication the user
 * simply needs to refresh/sign in again. A live incident: a user completed
 * a full research-interview conversation, clicked "Yes, test this", and
 * hit exactly that generic message with no actionable next step.
 */
export class SessionExpiredError extends Error {
  constructor(message: string = 'Your session could not be verified. Please refresh the page and sign in again to continue.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Fetch a fresh auth token, retrying a couple of times first -- Clerk's
 * `getToken()` can fail transiently (a brief network blip reaching Clerk's
 * token endpoint) well short of the underlying session actually having
 * expired, and retrying costs nothing a user would notice.
 *
 * - Resolves to the token on success (including "genuinely signed out",
 *   where it resolves to `null` so the caller can decide whether an
 *   anonymous request is acceptable for that endpoint).
 * - Throws `SessionExpiredError` only when `isSignedIn` is true but every
 *   retry still failed to produce a token -- a state Clerk's own SDK
 *   shouldn't normally produce, so treat it as the session having actually
 *   lapsed rather than silently proceeding unauthenticated.
 */
export async function getAuthTokenOrThrow(
  getToken: () => Promise<string | null>,
  isSignedIn: boolean | undefined,
  attempts: number = 2,
): Promise<string | null> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const token = await getToken();
      if (token) return token;
      break; // getToken() resolved with no token -- not a transient error, don't retry.
    } catch {
      if (attempt < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      }
    }
  }
  if (!isSignedIn) return null;
  throw new SessionExpiredError();
}

/**
 * Wrapper components that handle dev mode (no Clerk configured)
 * These render appropriately when Clerk is not available
 */

interface ChildrenProps {
  children?: React.ReactNode;
}

// SignedIn: In dev mode, always show children (user is "signed in")
export const SignedIn: React.FC<ChildrenProps> = ({ children }) => {
  if (!isClerkConfigured()) {
    return <>{children}</>;
  }
  return <ClerkSignedIn>{children}</ClerkSignedIn>;
};

// SignedOut: In dev mode, never show children (user is always "signed in")
export const SignedOut: React.FC<ChildrenProps> = ({ children }) => {
  if (!isClerkConfigured()) {
    return null;
  }
  return <ClerkSignedOut>{children}</ClerkSignedOut>;
};

// RedirectToSignIn: In dev mode, do nothing
export const RedirectToSignIn: React.FC = () => {
  if (!isClerkConfigured()) {
    return null;
  }
  return <ClerkRedirectToSignIn />;
};

// SignIn: In dev mode, show a message
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SignIn: React.FC<any> = (props) => {
  if (!isClerkConfigured()) {
    return <div className="p-5 text-center text-muted-foreground">Dev mode - auth disabled</div>;
  }
  return <ClerkSignIn {...props} />;
};

// SignUp: In dev mode, show a message
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SignUp: React.FC<any> = (props) => {
  if (!isClerkConfigured()) {
    return <div className="p-5 text-center text-muted-foreground">Dev mode - auth disabled</div>;
  }
  return <ClerkSignUp {...props} />;
};

// UserButton: In dev mode, show a simple avatar with dropdown menu
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserButton: React.FC<any> = (props) => {
  const [showMenu, setShowMenu] = React.useState(false);

  if (!isClerkConfigured()) {
    return (
      <div className="relative">
        <div 
          onClick={() => setShowMenu(!showMenu)}
          className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground cursor-pointer"
        >
          D
        </div>
        {showMenu && (
          <div className="absolute top-11 right-0 bg-popover border border-border rounded-lg py-2 min-w-[180px] shadow-lg z-[1000]">
            <div className="px-4 py-3 border-b border-border mb-2">
              <div className="font-semibold text-foreground text-sm">Developer User</div>
              <div className="text-muted-foreground text-xs">dev@localhost</div>
            </div>
            <div className="px-2 py-1 text-muted-foreground text-[11px] uppercase">
              Dev Mode
            </div>
            <div className="px-4 py-2 text-muted-foreground text-xs italic">
              Sign out disabled in dev mode.
              <br />
              Configure CLERK_PUBLISHABLE_KEY to enable auth.
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-red-400 mt-2 border-t border-border rounded-none"
              onClick={() => {
                setShowMenu(false);
                window.location.reload();
              }}
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh App
            </Button>
          </div>
        )}
      </div>
    );
  }
  return <ClerkUserButton {...props} />;
};
